import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, Search, MessageCircle, AlertTriangle, ChevronRight, Loader2,
  Mic, FileText, ChevronDown, X, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom searchable dropdown component
const SearchableSelect = ({ 
  placeholder, 
  value, 
  onChange, 
  options, 
  groupBy, 
  renderOption,
  emptyMessage = "No options found",
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  // Find selected option label
  const selectedOption = options.find(o => o.id === value);
  
  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const searchLower = search.toLowerCase();
    return options.filter(o => 
      o.name.toLowerCase().includes(searchLower) ||
      (o.shortName && o.shortName.toLowerCase().includes(searchLower))
    );
  }, [options, search]);
  
  // Group options if groupBy is provided
  const groupedOptions = useMemo(() => {
    if (!groupBy) return { '': filteredOptions };
    
    const groups = {};
    for (const option of filteredOptions) {
      const group = groupBy(option);
      if (!groups[group]) groups[group] = [];
      groups[group].push(option);
    }
    return groups;
  }, [filteredOptions, groupBy]);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0A7EA4]"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? (renderOption ? renderOption(selectedOption) : selectedOption.name) : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {Object.keys(groupedOptions).length === 0 || filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">{emptyMessage}</div>
            ) : (
              Object.entries(groupedOptions).map(([group, groupOptions]) => (
                <div key={group}>
                  {group && (
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0">
                      {group}
                    </div>
                  )}
                  {groupOptions.map(option => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        onChange(option.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                        value === option.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-700'
                      }`}
                    >
                      {renderOption ? renderOption(option) : option.name}
                      {value === option.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Hierarchical topic selector
const TopicTreeSelect = ({ topics, value, onChange, placeholder, testId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  
  // Build hierarchical structure
  const hierarchicalTopics = useMemo(() => {
    const parents = topics.filter(t => !t.parent_topic_id);
    const children = topics.filter(t => t.parent_topic_id);
    
    return parents.map(parent => ({
      ...parent,
      children: children.filter(c => c.parent_topic_id === parent.id)
    }));
  }, [topics]);
  
  // All parents expanded by default
  const [expandedParents, setExpandedParents] = useState(() => {
    return new Set(hierarchicalTopics.map(t => t.id));
  });
  
  // Update expanded parents when topics change
  useEffect(() => {
    setExpandedParents(new Set(hierarchicalTopics.map(t => t.id)));
  }, [hierarchicalTopics]);
  
  // Find selected topic
  const selectedTopic = topics.find(t => t.id === value);
  
  // Filter based on search
  const filteredTopics = useMemo(() => {
    if (!search.trim()) return hierarchicalTopics;
    
    const searchLower = search.toLowerCase();
    const filtered = [];
    
    for (const parent of hierarchicalTopics) {
      const parentMatches = parent.name.toLowerCase().includes(searchLower);
      const matchingChildren = parent.children?.filter(c => 
        c.name.toLowerCase().includes(searchLower)
      ) || [];
      
      if (parentMatches || matchingChildren.length > 0) {
        filtered.push({
          ...parent,
          children: parentMatches ? parent.children : matchingChildren
        });
      }
    }
    return filtered;
  }, [hierarchicalTopics, search]);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const toggleParent = (parentId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });
  };
  
  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-3 py-2 text-left bg-white border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#0A7EA4]"
      >
        <span className={selectedTopic ? 'text-gray-900' : 'text-gray-500'}>
          {selectedTopic ? selectedTopic.name : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                autoFocus
              />
              {search && (
                <button 
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {/* No specific topic option */}
            <button
              type="button"
              onClick={() => {
                onChange('none');
                setIsOpen(false);
                setSearch('');
              }}
              className={`flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                value === 'none' ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-500 italic'
              }`}
            >
              No specific topic
              {value === 'none' && <Check className="w-4 h-4 ml-auto" />}
            </button>
            
            {filteredTopics.length === 0 && search ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">No topics match "{search}"</div>
            ) : (
              filteredTopics.map(parent => (
                <div key={parent.id}>
                  {/* Parent topic */}
                  <div className="flex items-center">
                    {parent.children && parent.children.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleParent(parent.id)}
                        className="p-2 hover:bg-gray-100"
                      >
                        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                          expandedParents.has(parent.id) || search ? 'rotate-90' : ''
                        }`} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onChange(parent.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex-1 flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-gray-100 ${
                        value === parent.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-700'
                      } ${!parent.children?.length ? 'pl-10' : ''}`}
                    >
                      <span className="font-medium">{parent.name}</span>
                      {value === parent.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  </div>
                  
                  {/* Child topics - always visible since parents are expanded by default */}
                  {(expandedParents.has(parent.id) || search) && parent.children?.map(child => (
                    <button
                      key={child.id}
                      type="button"
                      onClick={() => {
                        onChange(child.id);
                        setIsOpen(false);
                        setSearch('');
                      }}
                      className={`flex items-center gap-2 w-full pl-12 pr-3 py-2 text-sm text-left hover:bg-gray-100 ${
                        value === child.id ? 'bg-[#0A7EA4]/10 text-[#0A7EA4]' : 'text-gray-600'
                      }`}
                    >
                      {child.name}
                      {value === child.id && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ForumNewQuestionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  
  const [functions, setFunctions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingSimilar, setCheckingSimilar] = useState(false);
  const [similarQuestions, setSimilarQuestions] = useState([]);
  const [showSimilar, setShowSimilar] = useState(false);
  const [confirmedNoDuplicate, setConfirmedNoDuplicate] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedFunction, setSelectedFunction] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [isMmdSpecific, setIsMmdSpecific] = useState(false);

  const fetchFunctions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/functions`);
      const filtered = response.data.filter(f => f.grade === preferences.grade);
      setFunctions(filtered);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  }, [preferences.grade]);

  const fetchTopics = useCallback(async () => {
    if (!selectedFunction) {
      setTopics([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/topics`, {
        params: { function_id: selectedFunction }
      });
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    }
  }, [selectedFunction]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  useEffect(() => {
    fetchTopics();
    setSelectedTopic('');
  }, [fetchTopics]);

  // Check for similar questions when title changes (debounced)
  useEffect(() => {
    if (title.length < 10 || !selectedFunction) {
      setSimilarQuestions([]);
      setShowSimilar(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setCheckingSimilar(true);
        const response = await axios.post(`${API}/forum/similar`, {
          title,
          function_id: selectedFunction,
          grade: preferences.grade
        });
        
        if (response.data.has_similar) {
          setSimilarQuestions(response.data.questions);
          setShowSimilar(true);
          setConfirmedNoDuplicate(false);
        } else {
          setSimilarQuestions([]);
          setShowSimilar(false);
        }
      } catch (error) {
        console.error('Failed to check similar questions:', error);
      } finally {
        setCheckingSimilar(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [title, selectedFunction, preferences.grade]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || title.length < 10) {
      toast.error('Title must be at least 10 characters');
      return;
    }
    
    if (!content.trim() || content.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    
    if (!selectedFunction) {
      toast.error('Please select a function');
      return;
    }
    
    if (showSimilar && !confirmedNoDuplicate) {
      toast.error('Please confirm that your question is not a duplicate');
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.post(`${API}/forum/questions`, {
        title: title.trim(),
        content: content.trim(),
        function_id: selectedFunction,
        topic_id: selectedTopic === 'none' ? null : (selectedTopic || null),
        is_mmd_specific: isMmdSpecific
      });
      
      toast.success('Question posted successfully!');
      navigate(`/forum/question/${response.data.id}`);
    } catch (error) {
      console.error('Failed to create question:', error);
      toast.error(error.response?.data?.detail || 'Failed to post question');
    } finally {
      setLoading(false);
    }
  };

  // Group functions by exam type (Oral/Written) for the dropdown
  const functionOptions = useMemo(() => {
    return functions.map(f => ({
      id: f.id,
      name: f.name,
      shortName: `F${f.function_number}${f.function_suffix || ''} - ${f.name}`,
      examType: f.exam_type || 'orals',
      functionNumber: f.function_number
    }));
  }, [functions]);

  const groupByExamType = (option) => {
    return option.examType === 'written' ? '✏️ Written Functions' : '🎤 Oral Functions';
  };

  const renderFunctionOption = (option) => (
    <div className="flex items-center gap-2">
      {option.examType === 'written' ? (
        <FileText className="w-4 h-4 text-purple-500" />
      ) : (
        <Mic className="w-4 h-4 text-[#0A7EA4]" />
      )}
      <span>F{option.functionNumber} - {option.name}</span>
    </div>
  );

  return (
    <Layout>
      <div className="page-transition max-w-3xl mx-auto">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/forum')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Forum
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#0A7EA4]" />
              Ask a Question
            </CardTitle>
            <CardDescription>
              Share your doubt with the {preferences.grade} community
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Function Selection - Grouped & Searchable */}
              <div className="space-y-2">
                <Label htmlFor="function">Function *</Label>
                <SearchableSelect
                  placeholder="Select a function"
                  value={selectedFunction}
                  onChange={setSelectedFunction}
                  options={functionOptions}
                  groupBy={groupByExamType}
                  renderOption={renderFunctionOption}
                  emptyMessage="No functions found"
                  testId="function-select"
                />
              </div>

              {/* Topic Selection - Hierarchical & Searchable */}
              {topics.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="topic">Topic (Optional)</Label>
                  <TopicTreeSelect
                    topics={topics}
                    value={selectedTopic}
                    onChange={setSelectedTopic}
                    placeholder="Select a topic"
                    testId="topic-select"
                  />
                </div>
              )}

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Question Title *</Label>
                <div className="relative">
                  <Input
                    id="title"
                    placeholder="What's your question? Be specific and clear..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    data-testid="title-input"
                  />
                  {checkingSimilar && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500">{title.length}/200 characters</p>
              </div>

              {/* Similar Questions Warning */}
              {showSimilar && similarQuestions.length > 0 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-amber-800 mb-2">
                          Similar questions already exist
                        </h4>
                        <p className="text-sm text-amber-700 mb-3">
                          Please check if your question has already been asked:
                        </p>
                        <div className="space-y-2 mb-3">
                          {similarQuestions.slice(0, 3).map(q => (
                            <a
                              key={q.id}
                              href={`/forum/question/${q.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-white rounded border border-amber-200 hover:border-amber-400 transition-colors"
                            >
                              <MessageCircle className="w-4 h-4 text-amber-600" />
                              <span className="text-sm text-gray-700 flex-1 truncate">{q.title}</span>
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            </a>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={confirmedNoDuplicate}
                            onChange={(e) => setConfirmedNoDuplicate(e.target.checked)}
                            className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-amber-700">
                            I&apos;ve checked and my question is different
                          </span>
                        </label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Content */}
              <div className="space-y-2">
                <Label htmlFor="content">Description *</Label>
                <Textarea
                  id="content"
                  placeholder="Provide more details about your question. Include any relevant context, what you've already tried, or specific scenarios you're unsure about..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  data-testid="content-input"
                />
                <p className="text-xs text-gray-500">{content.length}/5000 characters</p>
              </div>

              {/* MMD Specific Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="mmd-specific" className="font-medium">
                    {preferences.mmd_location} MMD Specific
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Enable if your question is specifically about your MMD&apos;s practices
                  </p>
                </div>
                <Switch
                  id="mmd-specific"
                  checked={isMmdSpecific}
                  onCheckedChange={setIsMmdSpecific}
                  data-testid="mmd-specific-toggle"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/forum')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#0A7EA4] hover:bg-[#0A7EA4]/90"
                  disabled={loading || (showSimilar && !confirmedNoDuplicate)}
                  data-testid="submit-question-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Question'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Guidelines */}
        <Card className="mt-6">
          <CardContent className="pt-4">
            <h3 className="font-semibold text-gray-900 mb-3">Tips for a great question:</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <Search className="w-4 h-4 text-[#0A7EA4] mt-0.5 flex-shrink-0" />
                <span>Search first to see if your question has already been answered</span>
              </li>
              <li className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 text-[#0A7EA4] mt-0.5 flex-shrink-0" />
                <span>Be specific and provide context about your situation</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="w-4 h-4 text-[#0A7EA4] mt-0.5 flex-shrink-0" />
                <span>Select the correct function and topic for better visibility</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ForumNewQuestionPage;
